/**
 * Created by Mikhail Menshenin on 22.10.2025
 */

GameBase.prototype.loadFromSnapshot = function () {
    const snapshot = cleverapps.loadedSnapshot;
    const data = snapshot && snapshot.games;

    if (data && !snapshot.gameWasLoaded && data.episode === this.level.episodeNo && data.level === this.level.levelNo) {
        Object.defineProperty(snapshot, "gameWasLoaded", {
            value: true,
            writable: true
        });
        return data;
    }
};

// "Only X% of players beat level Y" statistics minimal.
GameBase.DIFFICULTY_HINT_DELAY = 1000;
// The statistics minimal is shown at most once per this many levels (see needDifficultyHint).
GameBase.DIFFICULTY_HINT_LEVEL_STEP = 4;

DataLoader.TYPES.DIFFICULTY_HINT_LEVEL = "_difficulty_hint_level";

// Sit the statistics minimal lower on the screen (~half the default gap from the
// bottom edge; base dy is 160). centerHint's POSITIONS.BOTTOM slot is used only by
// this hint, so overriding it here does not affect other center hints.
cleverapps.styles.CenterHintView.positions[MinimalDialogue.POSITIONS.BOTTOM] = {
    x: { align: "center" },
    y: { align: "bottom", dy: 80 }
};

GameBase.prototype.showStartGameMessage = cleverapps.extendFunc(GameBase.prototype.showStartGameMessage, function (f) {
    if (this.level.isSuperHard() && bundles.gamemessage.jsons && bundles.gamemessage.jsons.super_hard_level_json) {
        this.showHardLevelAnimation(bundles.gamemessage.jsons.super_hard_level_json, "ui.gamemessage.superhardlevel", f);
        return;
    }

    if (this.level.isHard() && bundles.gamemessage.jsons && bundles.gamemessage.jsons.hard_level_json) {
        this.showHardLevelAnimation(bundles.gamemessage.jsons.hard_level_json, "ui.gamemessage.hardlevel", f);
        return;
    }

    this._super(f);
});

GameBase.prototype.getHardLevelAnimationName = function (animationSpine) {
    const lang = cleverapps.settings.language;

    if (animationSpine.hasAnimation(`animation_${lang}`)) {
        return `animation_${lang}`;
    }

    if (animationSpine.hasAnimation("animation_en")) {
        return "animation_en";
    }

    return "animation";
};

GameBase.prototype.showHardLevelAnimation = function (json, effect, f) {
    const styles = cleverapps.styles.HardLevelAnimation;
    const scene = cleverapps.Scenes.GetRunningScene();
    const animationSpine = new cleverapps.Spine(json);
    const animationName = this.getHardLevelAnimationName(animationSpine);

    animationSpine.setPosition(scene.width / 2, scene.height / 2);
    animationSpine.setScale(styles.scale);
    animationSpine.setLocalZOrder(styles.zOrder);
    cleverapps.Scenes.GetMovingNode().addChild(animationSpine);

    cleverapps.effects.fire(effect);
    cleverapps.shadow.moveDown();
    cleverapps.shadow.show();

    animationSpine.runAction(new cc.Sequence(
        new cc.SpineAction(animationSpine, animationName)
    )).setFinalize(() => {
        cleverapps.shadow.hide();
        animationSpine.removeFromParent();
        f();
    });
};

cleverapps.styles.HardLevelAnimation = {
    scale: 1,
    zOrder: 17
};

// Run the statistics hint after showStartGameMessage instead of before it, so on
// hard levels the "Hard level" intro (GameMessageView ribbon / hard_level spine
// animation) plays and disappears before the statistics minimal is scheduled.
// NOTE: mirrors GameBase.prototype.listIntroActions (cleverapps/levels/game/gamebase.js)
// with showDifficultyHint moved one slot down; keep the list in sync.
GameBase.prototype.listIntroActions = cleverapps.extendFunc(GameBase.prototype.listIntroActions, function () {
    const actions = [
        this.gameplayStartIntroAction.bind(this),
        this.incCounter.bind(this),
        this.startBoosters.bind(this),
        this.showScreen.bind(this),
        this.restoreProgressUpdate.bind(this),
        this.runBeforeStartActions.bind(this),
        this.showStartGameMessage.bind(this),
        this.showDifficultyHint.bind(this),
        this.decCounter.bind(this),
        this.startIntroTutorial.bind(this),
        this.startOldTutorial.bind(this),
        this.runAfterStartActions.bind(this)
    ];

    if (cleverapps.config.debugMode && this._super().length !== actions.length) {
        cleverapps.throwAsync("aisensia listIntroActions override is out of sync with GameBase.listIntroActions");
    }

    return actions;
});

// Overrides GameBase.prototype.needDifficultyHint (cleverapps/levels/game/gamebase.js).
// The guard clauses below MIRROR the base implementation; keep them in sync.
// Behaviour change: instead of "every hard level + 25% of regular levels", the
// statistics minimal is shown at most once per GameBase.DIFFICULTY_HINT_LEVEL_STEP
// levels (cooldown by level number, persisted across sessions).
GameBase.prototype.needDifficultyHint = function () {
    if (cleverapps.config.debugMode && cleverapps.flags.statisticHintShow) {
        return true;
    }

    if (cleverapps.tutorial.getAvailableScenario() || this.levelWithTutorial()) {
        return false;
    }

    if (!this.level.isRegular() && !this.level.isHard()) {
        return false;
    }

    if (cleverapps.config.type === "board" && !this.emptySave) {
        return false;
    }

    if (Game.currentGame.getMode() === GameBase.MODE_HIGHSCORE) {
        return false;
    }

    if (!cleverapps.user.checkAvailable(cleverapps.config.availables.STATISTICS_MINIMAL)) {
        return false;
    }

    const lastLevel = cleverapps.dataLoader.load(DataLoader.TYPES.DIFFICULTY_HINT_LEVEL);
    if (typeof lastLevel === "number"
        && this.level.getHumanReadableNumber() - lastLevel < GameBase.DIFFICULTY_HINT_LEVEL_STEP) {
        return false;
    }

    return true;
};

GameBase.prototype.markDifficultyHintShown = function () {
    cleverapps.dataLoader.save(DataLoader.TYPES.DIFFICULTY_HINT_LEVEL, this.level.getHumanReadableNumber());
};

GameBase.prototype.showDifficultyHint = function (f) {
    if (this.needDifficultyHint()) {
        this.markDifficultyHintShown();

        cleverapps.centerHint.schedule(GameBase.DIFFICULTY_HINT_DELAY, () => cleverapps.centerHint.createTextHint(Messages.get("StatisticsHint.Text", {
            percent: this.calcCompletePercent(this.level.getHumanReadableNumber()),
            level: cleverapps.user.getVirtualLevel()
        }), MinimalDialogue.POSITIONS.BOTTOM));
    }

    f();
};

GameBase.prototype.getNormalizedLevelReward = function (rewardConfig) {
    const levelTag = this.level.getRewardTag();
    const rewardsAmount = rewardConfig[levelTag] || rewardConfig.default;
    const integerPart = Math.floor(rewardsAmount);
    const fractionalPart = rewardsAmount - integerPart;
    const randomChance = Math.random();
    const finalReward = (fractionalPart > randomChance) ? integerPart + 1 : integerPart;
    return finalReward;
};
